import type { Schemas } from '@kanos/api-client';
import { Button } from '@kanos/ui';
import { api, failure, ProblemAlert } from '@kanos/web-core';
import { useMutation } from '@tanstack/react-query';
import { CircleCheck, IdCard } from 'lucide-react';
import { useState } from 'react';
import { useBranding } from './branding';
import { takeLinkToken } from './link-token';
import { Lead, PortalPage } from './portal-page';

type IdentificationStarted = Schemas['IdentificationStarted'];

const HERE = '/identify';

/** Hands the person to the identification service's page for this link. */
function useStartIdentification(token: string | null) {
  return useMutation({
    mutationFn: async (): Promise<IdentificationStarted> => {
      const { data, error, response } = await api.POST('/api/v1/portal/identification/start', {
        body: { token: token ?? '' },
      });
      if (!data) throw failure('startRemoteIdentification', error, response.status);
      return data;
    },
    onSuccess: ({ redirectUrl }) => window.location.assign(redirectUrl),
  });
}

/**
 * The link of a remote identification (ADR-031): what is about to happen, and on to the
 * identification service - with the online ID card, or by video where that is not possible.
 * No account is needed; the link is the person's own.
 */
export function IdentifyPage() {
  const branding = useBranding();
  const [token] = useState(() => takeLinkToken(HERE));
  const start = useStartIdentification(token);
  const firm = branding?.firmName ?? 'Ihre Kanzlei';

  return (
    <PortalPage branding={branding}>
      {token === null ? (
        <Lead title="Ihre Identifizierung">
          Bitte öffnen Sie den Link aus der E-Mail Ihrer Kanzlei erneut – er bringt Sie hierher
          zurück.
        </Lead>
      ) : (
        <section className="grid gap-6">
          <IdCard aria-hidden className="size-10 text-accent-text" />
          <Lead title="Online identifizieren">
            {firm} muss Sie nach dem Geldwäschegesetz identifizieren. Das ist bei jedem neuen Mandat
            gesetzlich vorgeschrieben – kein Misstrauen Ihnen gegenüber.
          </Lead>
          <p>
            Dafür geht es weiter zu einem zugelassenen Identifizierungsdienst: am einfachsten mit
            der Online-Ausweisfunktion Ihres Personalausweises und dem Smartphone, sonst per
            Video-Gespräch mit Ihrem Ausweis. Das dauert meist etwa 5 Minuten, per Video etwas
            länger.
          </p>
          <p className="text-sm text-muted">
            Halten Sie Ihren Ausweis bereit. Der Dienst übermittelt das Ergebnis an Ihre Kanzlei.
          </p>
          {start.isError && <ProblemAlert error={start.error} />}
          <Button
            className="justify-self-start"
            isPending={start.isPending || start.isSuccess}
            onPress={() => start.mutate()}
          >
            Weiter zur Identifizierung
          </Button>
        </section>
      )}
    </PortalPage>
  );
}

/** Back from the identification service. */
export function IdentifyDonePage() {
  const branding = useBranding();
  return (
    <PortalPage branding={branding}>
      <section aria-live="polite" className="glass grid gap-4 rounded-lg p-6 sm:p-8">
        <CircleCheck aria-hidden className="size-10 text-success-fg" />
        <h1 className="text-3xl font-semibold tracking-[-0.03em]">Danke.</h1>
        <p className="text-muted sm:text-lg">
          Der Identifizierungsdienst übermittelt das Ergebnis an Ihre Kanzlei. Sie können dieses
          Fenster schließen; fehlt noch etwas, meldet sich die Kanzlei bei Ihnen.
        </p>
      </section>
    </PortalPage>
  );
}
